/**
 * InfoTooltip styles – reusable (i) icon + tooltip popover / modal.
 * Works in light and dark mode (uses background.card and contrasting text).
 */

import { StyleSheet, Platform } from 'react-native';
import type { ThemeColors } from '../../../theme/themes';

/** Width below which tooltip/modal use compact sizing. */
export const TOOLTIP_NARROW_BREAKPOINT = 400;

export interface InfoTooltipStyleOptions {
  narrow?: boolean;
  isDark?: boolean;
}

export const getInfoTooltipStyles = (
  theme: ThemeColors,
  options?: InfoTooltipStyleOptions
) => {
  const narrow = options?.narrow === true;
  const isDark = options?.isDark === true;
  const tooltipBg = theme.background.card;
  const tooltipTextColor = isDark ? theme.gray[600] : theme.gray[900];

  return StyleSheet.create({
    wrapper: {
      position: 'relative' as const,
      padding: 4,
      justifyContent: 'center',
      alignItems: 'center',
    },
    iconButton: {
      padding: 4,
      justifyContent: 'center',
      alignItems: 'center',
    },
    iconButtonPressed: {
      opacity: 0.7,
    },
    iconButtonHover: {
      opacity: 0.85,
    },
    /** Wrapper for custom trigger (ref for positioning; no (i) icon). No alignSelf so parent can center it. */
    triggerWrapper: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    /** Pressable wrapping custom trigger so it receives touch. */
    triggerTouchable: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    /** Full-screen transparent backdrop when tooltip is open on tap (web); tap to close. */
    tooltipBackdrop: {
      position: 'fixed' as 'absolute',
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
      zIndex: 10000,
    },
    /** Web-only: tooltip wrapper (position only); contains tooltipBubble + tooltipPointer. */
    tooltipPortal: {
      position: 'fixed' as 'absolute',
      minWidth: narrow ? 160 : 200,
      maxWidth: narrow ? 220 : 280,
      paddingBottom: 8,
      zIndex: 10001,
    },
    /** Rounded bubble body; pointer sits below and right. */
    tooltipBubble: {
      paddingVertical: narrow ? 8 : 10,
      paddingHorizontal: narrow ? 10 : 12,
      borderRadius: 12,
      backgroundColor: tooltipBg,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 4px 12px rgba(0,0,0,0.25)' }
        : {}),
    },
    /** Triangle pointer from bottom-right of bubble toward trigger (tooltipBg color). */
    tooltipPointer: {
      position: 'absolute' as const,
      bottom: 0,
      right: 14,
      width: 0,
      height: 0,
      borderLeftWidth: 6,
      borderRightWidth: 6,
      borderTopWidth: 8,
      borderLeftColor: 'transparent',
      borderRightColor: 'transparent',
      borderTopColor: tooltipBg,
    },
    tooltipText: {
      fontSize: narrow ? 12 : 13,
      fontWeight: '500',
      color: tooltipTextColor,
      textAlign: 'center',
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'transparent',
      justifyContent: 'center',
      alignItems: 'center',
      padding: narrow ? 16 : 24,
    },
    /** Fills overlay for tap-to-close; separate from modalOverlay so outer View can use onTouchMove for scroll-to-close. */
    modalOverlayTouchable: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
    },
    modalContent: {
      paddingVertical: narrow ? 10 : 14,
      paddingHorizontal: narrow ? 12 : 16,
      borderRadius: narrow ? 10 : 12,
      backgroundColor: tooltipBg,
      maxWidth: narrow ? 280 : 320,
      ...(Platform.OS !== 'web' && {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 8,
        elevation: 12,
      }),
    },
    modalText: {
      fontSize: narrow ? 13 : 14,
      fontWeight: '500',
      color: tooltipTextColor,
      textAlign: 'center',
    },
  });
};

/** Gap between tooltip bubble and trigger icon so tooltip sits clearly above, not overlapping. */
const TOOLTIP_OFFSET_PX = 14;
const TOOLTIP_ESTIMATED_HEIGHT = 120;
const TOOLTIP_ESTIMATED_HEIGHT_NARROW = 100;
const VIEWPORT_PADDING = 12;

/**
 * Returns clamped position for the portal tooltip.
 * Tooltip is placed above the button with its bottom-right aligned to the button's top-right
 * (tip of tooltip points at top-right of button); then clamped to viewport.
 * @param estimatedHeightOverride - When provided, use instead of default (e.g. from content length: long text = larger value so bubble doesn't overlap).
 */
export const getTooltipPortalPosition = (
  rect: { left: number; top: number; bottom: number; right: number },
  viewportWidth: number,
  viewportHeight: number,
  tooltipMaxWidth: number = 280,
  estimatedHeightOverride?: number
): { left: number; top: number } => {
  const defaultHeight =
    tooltipMaxWidth <= 220 ? TOOLTIP_ESTIMATED_HEIGHT_NARROW : TOOLTIP_ESTIMATED_HEIGHT;
  const estimatedHeight = estimatedHeightOverride ?? defaultHeight;
  const maxLeft = viewportWidth - VIEWPORT_PADDING - tooltipMaxWidth;
  let left = rect.right - tooltipMaxWidth;
  let top = rect.top - estimatedHeight - TOOLTIP_OFFSET_PX;
  if (left > maxLeft) left = maxLeft;
  if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
  if (top < VIEWPORT_PADDING) top = VIEWPORT_PADDING;
  if (top + estimatedHeight > viewportHeight - VIEWPORT_PADDING) {
    top = Math.max(VIEWPORT_PADDING, viewportHeight - VIEWPORT_PADDING - estimatedHeight);
  }
  return { left, top };
};
